/**
 * Shared prompt-text draft store.
 *
 * Use this store for unsent prompt contents (`draftMessage`) that need to be
 * seeded, read, or cleared across chat surfaces and controllers, such as
 * RovoChatPromptInput, inline edit, chat-only/staging creation modals, sticky
 * skill handling, widget/JSM textareas, and CSM simulator inputs.
 *
 * Do not use it as a catch-all for chat-prompt UI state that only affects how
 * the current prompt input submission is sent. Keep that current-submission
 * UI/routing state with the package that owns the input UI and send behavior;
 * for Rovo Chat prompt-input state, that package is
 * @atlassian/conversation-assistant-chat-prompt-input.
 */
import { useCallback } from 'react';

import { createHook, createStore, defaultRegistry } from 'react-sweet-state';

import type { DocNode } from '@atlaskit/adf-schema/doc';

type Store = {
	drafts: { [conversationId: string]: DocNode | null | undefined };
};

const store = createStore({
	initialState: {
		drafts: {},
	} as Store,
	actions: {
		setDraftMessage:
			(conversationId: string, draftMessage: DocNode | null) =>
			({ setState, getState }) => {
				const { drafts } = getState();
				setState({
					drafts: {
						...drafts,
						[conversationId]: draftMessage,
					},
				});
			},
	},
});

const useStore = createHook(store);

/**
 * Set a draft message from outside React (e.g. from a chat-store action).
 * Always overwrites the existing draft (if any) for `conversationId`.
 */
export const setDraftMessage = (conversationId: string, draftMessage: DocNode | null): void => {
	defaultRegistry.getStore(store).actions.setDraftMessage(conversationId, draftMessage);
};

export const useDraftsController = (
	conversationId: string,
): {
	draftMessage: DocNode | null | undefined;
	setDraftMessage: (draftMessage: DocNode | null) => void;
} => {
	const [{ drafts }, { setDraftMessage: storeSetDraftMessage }] = useStore();

	const setDraftMessage = useCallback(
		(draftMessage: DocNode | null): void => {
			storeSetDraftMessage(conversationId, draftMessage);
		},
		[conversationId, storeSetDraftMessage],
	);

	return {
		draftMessage: drafts[conversationId],
		setDraftMessage,
	};
};
